// Libraries
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { components } from 'react-select';
import { toast } from 'react-toastify';

// Components
import { FormElems, CustomSelect, Alert } from '..';

import {
    updateBinDetailRequest,
    updateBinDetailError,
} from '../../containers/ManageBin/redux/manageBin.actions';

// Utils
import { getBinOptions } from '../../utils/helperMethods';

// Constant
import { SUCCESS_MESSAGES } from '../../utils/constants/toastMessages';

// Validator
import validate from './validator';

// Styles
import './editBin.scss';

/**
 * EditBin Component
 *
 * @param {Object} props - React component props
 */

const radioOptions = [
    {
        name: 'True',
        value: true,
    },
    {
        name: 'False',
        value: false,
    },
];

const SingleValue = ({ children, ...props }) => {
    const { data } = props;
    return (
        <components.SingleValue {...props}>
            <div className="selectWith-image">
                <div className="img-wrap">
                    <img src={data.image} alt={data.value} />
                </div>
                <label>{data.label}</label>
            </div>
        </components.SingleValue>
    );
};

const Option = props => {
    const { data } = props;
    return (
        <components.Option {...props}>
            <div className="selectWith-image">
                <div className="img-wrap">
                    <img src={data.image} alt={data.value} />
                </div>
                <label>{data.label}</label>
            </div>
        </components.Option>
    );
};

const renderCustomSelect = props => {
    const { input, data, ...attributes } = props;
    const handleBlur = () => input.onBlur;
    return (
        <div>
            <CustomSelect
                instanceId={input.name}
                {...input}
                {...attributes}
                value={data}
                onBlur={handleBlur}
            />
        </div>
    );
};

const EditBin = props => {
    const {
        actions,
        closePopup,
        handleSubmit,
        staticData,
        initialValues,
        manageBin,
        dirty,
    } = props;
    const { payValues } = staticData;
    const { updateBin } = manageBin;
    const [selectedCardType, setSelectedCardType] = useState([]);
    const [cardOptions, setCardOptions] = useState([]);

    useEffect(() => {
        if (updateBin.isSuccess) {
            toast.info(SUCCESS_MESSAGES.UPDATE_DATA('Bin'));
            closePopup(true);
        }
        return () => {
            actions.updateBinDetailError({ error: {} });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateBin.isSuccess]);

    useEffect(() => {
        if (payValues.CARD_TYPE) {
            setCardOptions(getBinOptions(payValues.CARD_TYPE));
        }
    }, [payValues.CARD_TYPE]);

    useEffect(() => {
        if (initialValues && initialValues.cardType) {
            const selectedValue = cardOptions.filter(
                item => item.value === initialValues.cardType
            );
            setSelectedCardType(selectedValue);
        }
    }, [cardOptions, initialValues]);

    const onCancel = (onSuccess, entityType) => {
        closePopup(onSuccess, entityType);
    };

    /**
     * Method to handle form submit binValues for EditBin
     *
     *  @param {object} binValues
     * @returns {null}
     */
    const handleFormSubmit = binValues => {
        const binValuesCopy = { ...binValues };
        if (binValuesCopy.cardType) {
            binValuesCopy.cardType = selectedCardType[0].value;
        }
        const request = {
            cardBinRangevo: binValuesCopy,
        };
        actions.updateBinDetailRequest(request);
    };

    /**
     * Method to handle cardType selection
     *
     *  @param {array} value
     * @returns {null}
     */
    const handleBankSelect = value => {
        setSelectedCardType([value]);
    };
    const getCardNatureOption = values => {
        const cardNature = values;
        if (cardNature && cardNature.NB) {
            delete cardNature.NB;
        }
        return getBinOptions(cardNature) || [];
    };

    return (
        <form className="bin-view-edit">
            {updateBin && updateBin.error && updateBin.error.errorMessage ? (
                <Alert customClass="error-msg">
                    {updateBin.error.errorMessage}
                </Alert>
            ) : null}
            <div className="form-section">
                <div className="form-field">
                    <p className="label">Card Type</p>
                    <Field
                        name="cardType"
                        customClass="cardType-select"
                        options={cardOptions}
                        isSearchable={false}
                        components={{ Option, SingleValue }}
                        data={selectedCardType}
                        onChange={handleBankSelect}
                        closeMenuOnSelect={true}
                        component={renderCustomSelect}
                        placeholder="Select Card Type"
                        isDisabled={false}
                    />
                </div>
            </div>
            <div className="form-section">
                <div className="form-field">
                    <p className="label">Card Nature</p>
                    <Field
                        name="cardNature"
                        data={getCardNatureOption(payValues.CARD_NATURE)}
                        labelField="label"
                        valueField="value"
                        label="Select Card Nature"
                        component={FormElems.Select}
                    />
                </div>
            </div>
            <div className="form-section">
                <div className="form-field">
                    <p className="label">Issuing Bank</p>
                    <Field
                        name="issuingBank"
                        data={getBinOptions(payValues.BANK_CODE) || []}
                        labelField="label"
                        valueField="value"
                        label="Select Issuing Bank"
                        component={FormElems.Select}
                    />
                </div>
            </div>
            <div className="wrap-forms-input">
                <div className="form-section">
                    <div className="form-field">
                        <p className="label">Domestic</p>
                        <Field
                            name="isDomestic"
                            wrapperClass="buttonMode"
                            options={radioOptions}
                            labelField="name"
                            valueField="value"
                            component={FormElems.Radio}
                        />
                    </div>
                </div>
                <div className="form-section">
                    <div className="form-field">
                        <p className="label">EMI Bin</p>
                        <Field
                            name="emiBin"
                            wrapperClass="buttonMode"
                            options={radioOptions}
                            labelField="name"
                            valueField="value"
                            component={FormElems.Radio}
                        />
                    </div>
                </div>
                <div className="form-section">
                    <div className="form-field">
                        <p className="label">Bin Block</p>
                        <Field
                            name="binBlocked"
                            wrapperClass="buttonMode"
                            options={radioOptions}
                            labelField="name"
                            valueField="value"
                            component={FormElems.Radio}
                        />
                    </div>
                </div>
            </div>
            <div className="form-section action">
                <FormElems.Button
                    customClass="cancel-button"
                    buttonType="secondary"
                    text="CANCEL"
                    type="button"
                    handleClick={onCancel}
                />
                <FormElems.Button
                    buttonType="primary"
                    text="UPDATE"
                    isDisable={!dirty}
                    handleClick={handleSubmit(handleFormSubmit)}
                />
            </div>
        </form>
    );
};

const mapStateToProps = ({ auth, form, staticData, manageBin }) => ({
    auth,
    form,
    staticData,
    manageBin,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        { updateBinDetailRequest, updateBinDetailError },
        dispatch
    ),
});

EditBin.propTypes = {
    actions: PropTypes.object,
    actionType: PropTypes.string,
    auth: PropTypes.object,
    closePopup: PropTypes.func,
    fetchData: PropTypes.func,
    form: PropTypes.object,
    staticData: PropTypes.object,
    handleSubmit: PropTypes.func,
    initialValues: PropTypes.object,
    manageBin: PropTypes.object,
    dirty: PropTypes.bool,
};

Option.propTypes = {
    data: PropTypes.object.isRequired,
    isSelected: PropTypes.bool,
};

SingleValue.propTypes = {
    data: PropTypes.object,
    children: PropTypes.array,
};

export default compose(
    reduxForm({
        form: 'edit-bin',
        validate,
        enableReinitialize: true,
    }),
    connect(mapStateToProps, mapDispatchToProps)
)(EditBin);
